import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { CAR_RENTAL_DUPLICATION_FIELDS } from '@/constants/benefitFields';
import BenefitDeletionModal from '@/components/benefits/BenefitDeletionModal';
import {
  useDuplicateCarRental,
  useUpdateCarRental,
} from '@/hooks/useCarRental';
import { CarRentalResponse } from '@/types/benefits/carRental/CarRentalResponse';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { getFileKeyFromUrl } from '@/utils/s3/getFileKeyFromUrl';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { ArrowRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import BenefitTypeCell from '../benefits/BenefitTypeCell';
import StyledButton from '../styled/StyledButton';
import StyledButtonLoader from '../styled/StyledButtonLoader';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import { formatBenefitCode } from '@/utils/displayCodes';
import StyledStepper from '../styled/StyledStepper';
import HorizontalLine from '../ui/HorizontalLine';
import ModalDuplication from '../ui/ModalDuplication';
import CarRentalCategoriesList from './categories/CarRentalCategoriesList';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';
import { CAR_RENTAL_STEPS } from '@/constants/steppers/carRentalSteps';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';
import { loadCarRentalDefaultValues } from '@/utils/benefits/car-rentals/loadCarRentalDefaultValues';
import {
  CarRentalFormDataWithTranslations,
  CarRentalSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '../translation/LanguageSwitchWithTranslateBtn';
import CarRentalPricingSummary from './CarRentalPricingSummary';
import CarRentalConfiguration from './CarRentalConfiguration';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

interface ViewCarRentalFormProps {
  carRental: CarRentalResponse;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  setLanguage: React.Dispatch<React.SetStateAction<SupportedLocale>>;
}

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<CarRentalFormDataWithTranslations>;

const ViewCarRentalForm = ({
  carRental,
  suppliers,
  language,
  setLanguage,
}: ViewCarRentalFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [currStep, setCurrStep] = useState(0);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);

  const methods = useForm<CarRentalFormDataWithTranslations>({
    resolver: zodResolver(CarRentalSchemaWithTranslations),
    mode: 'onChange',
    defaultValues: loadCarRentalDefaultValues(carRental),
  });

  const {
    control,
    reset,
    formState: { isValid, errors },
    handleSubmit,
    getValues,
    setValue,
  } = methods;

  const selectedCountry = useWatch({ control, name: 'country' });
  const currentName = useWatch({
    control,
    name: 'name',
    defaultValue: carRental.benefit.name,
  });
  const selectedSupplier = useWatch({
    control,
    name: 'supplier',
    defaultValue: {
      label: carRental.benefit.supplier.name,
      value: carRental.benefit.supplier.id,
    },
  });
  const carCategories = useWatch({ control, name: 'carCategories' });

  const { mutateAsync: updateCarRental } = useUpdateCarRental(language);
  const { mutateAsync: duplicateCarRental } = useDuplicateCarRental();

  const hasAtLeastOneCategory = useMemo(
    () =>
      carCategories && carCategories.length > 0
        ? !!(carCategories[0].model && carCategories[0].category)
        : false,
    [carCategories],
  );

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: ['benefit.name'] as const,
    nestedFields: {
      carCategories: [
        'category',
        'model',
        'description',
        'included',
        'notIncluded',
      ],
      insurances: ['name', 'shortDescription', 'longDescription'],
      options: ['name', 'description'],
      taxes: ['name'],
      oneWayFees: ['name', 'description'],
    },
    doublyNestedFields: {
      carCategories: {
        insurances: ['name', 'shortDescription', 'longDescription'],
        options: ['name', 'description'],
        taxes: ['name'],
        oneWayFees: ['name', 'description'],
      },
    },
  });

  const categoryErrorIndex = useMemo(() => {
    if (errors.carCategories) {
      return +Object.keys(errors.carCategories)[0];
    }
    return null;
  }, [errors]);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined)
        formData.append('marginRate', String(parsedMarginRate.data));
      addIf(data.displayGlobalForm, 'displayGlobalForm');
      addIf(data.shouldLimitInsurance, 'shouldLimitInsurance');
      if (data.insurances)
        formData.append('insurances', JSON.stringify(data.insurances));
      if (data.taxes) formData.append('taxes', JSON.stringify(data.taxes));
      if (data.options)
        formData.append('options', JSON.stringify(data.options));
      if (data.oneWayFees)
        formData.append('oneWayFees', JSON.stringify(data.oneWayFees));
      if (data.carCategories)
        formData.append(
          'carCategories',
          JSON.stringify(
            data.carCategories.map((carCategory) => ({
              ...carCategory,
              displayCategoryOptions:
                carCategory.displayCategoryOptions === 'true',
              pricings: carCategory.tarifs,
              tarifs: undefined,
              numberOfFiles: (carCategory.images ?? []).filter(
                (image) => image instanceof Blob,
              ).length,
              numberOfImageUrls: (carCategory.images ?? []).filter(
                (image) => !(image instanceof Blob),
              ).length,
              images: undefined,
            })),
          ),
        );
      data.carCategories
        .map((category) => category.images)
        .flat()
        .filter((image) => image instanceof Blob)
        .forEach((image) => {
          if (image) formData.append('files', image);
        });
      data.carCategories
        .map((category) => category.images)
        .flat()
        .filter((image) => !(image instanceof Blob))
        .forEach((imageUrl) => {
          if (imageUrl)
            formData.append(
              'imageUrls[]',
              getFileKeyFromUrl(imageUrl as string),
            );
        });
      formData.append('translations', JSON.stringify(data.translations));
      await toastAsync(
        updateCarRental({
          benefitId: carRental.benefit.id,
          data: formData,
        }),
        { success: t('benefits.benefitUpdatedSuccess') },
      );
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  const getTranslatedConst = useTranslatedConst();

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || carRental.benefit.name || 'Location de voiture'}
          code={formatBenefitCode(carRental.benefit.code)}
          label={<BenefitTypeCell type={carRental.benefit.type} />}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('common.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('common.delete')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2 text-gray-800 border-gray-300"
            onClick={() => setIsDeletionModalOpen(true)}
          />
          <StyledButton
            variant="outlined"
            title={t('common.duplicate')}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setIsDuplicationModalOpen(true)}
            icon={<CopyIcon className="w-5 h-5 stroke-brand-600" />}
          />
          {!isEditEnabled ? (
            <StyledButton
              title={t('common.edit')}
              onClick={() => setIsEditEnabled(true)}
              className="font-semibold gap-1 px-3 py-2"
              icon={<EditIcon className="w-5 h-5 stroke-white" />}
            />
          ) : (
            <>
              <StyledButton
                title={t('common.cancel')}
                onClick={() => {
                  setIsEditEnabled(false);
                  reset();
                }}
                className="font-semibold gap-1 px-3 py-2"
              />
              <StyledButtonLoader
                title={t('common.save')}
                successTitle={t('common.draftSaved')}
                onClick={
                  carRental.benefit.status === SupplierStatus.DRAFT
                    ? () => {
                        setIsEditEnabled(false);
                        submitForm(true)(getValues());
                      }
                    : handleSubmit(submitForm(true))
                }
                disabled={
                  carRental.benefit.status === SupplierStatus.DRAFT
                    ? currentName == undefined ||
                      selectedSupplier == undefined ||
                      selectedCountry == undefined
                    : !isValid
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </>
          )}
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(CAR_RENTAL_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({
              allNestedFields: true,
              translateToAllLocales: true,
            })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
          hideTranslateBtn={!isEditEnabled}
        />
        {currStep === 0 ? (
          <>
            <CarRentalConfiguration
              control={control}
              suppliers={suppliers}
              isEditEnabled={isEditEnabled}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
            />
            <div className="flex flex-row-reverse p-6">
              <StyledButton
                title={
                  hasAtLeastOneCategory
                    ? t('carRental.viewModifyCarCategories')
                    : t('carRental.addCarCategory')
                }
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedCountry == undefined
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
              />
            </div>
          </>
        ) : currStep === 1 ? (
          <>
            <CarRentalCategoriesList
              control={control}
              categoryErrorIndex={categoryErrorIndex}
              isViewMode={!isEditEnabled}
              language={language}
            />
            {isEditEnabled && (
              <div className="flex flex-row-reverse p-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    carRental.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          setIsEditEnabled(false);
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    carRental.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            {carRental.benefit.status === SupplierStatus.DRAFT && (
              <div className="flex flex-row-reverse p-6">
                <StyledButtonLoader
                  title={t('carRental.publishCarRental')}
                  className="font-semibold gap-1 px-3 py-2"
                  successTitle={t('common.publishedSuccess')}
                  hasSucceeded={!errorsLength}
                  errorTitle={t('common.checkRequiredFields')}
                  onClick={handleSubmit(submitForm(false))}
                  icon={
                    <PlusIcon
                      className={clsx('w-5 h-5', {
                        'fill-white': isValid,
                        'fill-gray-400': !isValid,
                      })}
                    />
                  }
                />
              </div>
            )}
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(2);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <CarRentalPricingSummary control={control} />
            {isEditEnabled && (
              <div className="flex flex-row-reverse px-6 pb-6">
                <StyledButtonLoader
                  title={t('common.save')}
                  successTitle={t('common.draftSaved')}
                  onClick={
                    carRental.benefit.status === SupplierStatus.DRAFT
                      ? () => {
                          submitForm(true)(getValues());
                        }
                      : handleSubmit(submitForm(true))
                  }
                  disabled={
                    carRental.benefit.status === SupplierStatus.DRAFT
                      ? currentName == undefined ||
                        selectedSupplier == undefined ||
                        selectedCountry == undefined
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            {carRental.benefit.status === SupplierStatus.DRAFT &&
              isEditEnabled && (
                <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
                  <StyledButtonLoader
                    title={t('carRental.publishCarRental')}
                    className="font-semibold gap-1 px-3 py-2"
                    onClick={handleSubmit(submitForm(false))}
                    successTitle={t('common.publishedSuccess')}
                    hasSucceeded={!errorsLength}
                    errorTitle={t('common.checkRequiredFields')}
                    icon={
                      <PlusIcon
                        className={clsx('w-5 h-5', {
                          'fill-white': isValid,
                          'fill-gray-400': !isValid,
                        })}
                      />
                    }
                    iconPosition="right"
                  />
                </div>
              )}
          </>
        )}
        {isDeletionModalOpen && carRental.benefit.id && (
          <BenefitDeletionModal
            benefitId={carRental.benefit.id}
            closeModal={() => {
              setIsDeletionModalOpen(false);
            }}
            onDeleted={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
          />
        )}
        {isDuplicationModalOpen && (
          <ModalDuplication
            actionOnClick={async (selectedFields) => {
              if (carRental.benefitId) {
                const duplicatedCarRental = await duplicateCarRental({
                  benefitId: carRental.benefit.id,
                  fields: selectedFields.flatMap((fields) => fields.split(';')),
                });
                navigate(
                  PATHS.home.benefits.carRental.view.replace(
                    ':benefitId',
                    duplicatedCarRental.benefitId,
                  ),
                  { state: location.state },
                );
              }
            }}
            closeModal={() => {
              setIsDuplicationModalOpen(false);
            }}
            fields={getTranslatedConst(CAR_RENTAL_DUPLICATION_FIELDS).concat(
              carRental.carCategories.map((category, index) => ({
                label:
                  category.category && category.model
                    ? `${category.category}- ${category.model}`
                    : `Véhicule ${index + 1}`,
                value: `carCategories;${category.id}`,
              })),
            )}
            type="prestation"
            code={carRental.benefit.code}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default ViewCarRentalForm;
